/* eslint-disable @typescript-eslint/no-unused-vars */
import clsx from 'clsx';
import { FC, ReactNode } from 'react';
import { RegisterOptions, useController, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { Label } from '../Label';
import { Tooltip } from '../tooltip/Tooltip';

export const get = (path: string, value?: Record<string, any>, defaultValue?: any): any => {
  return path.split('.').reduce((acc, v) => {
    try {
      acc = acc?.[v];
    } catch (e) {
      return defaultValue;
    }
    return acc;
  }, value);
};

export interface InputProps<T> {
  id?: string;
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  placeholderClass?: string;
  className?: string;
  readOnly?: boolean;
  onKeyDown?: (value: any) => void;
}

export interface CustomFieldErrorParser {
  primaryId: string;
  secondaryId: string;
  index: number;
}

interface Props {
  id: string;
  name: string;
  description?: string;
  label?: string | ReactNode;
  readOnly?: boolean;
  clean?: boolean;
  inputClassName?: string;
  required?: boolean;
  validator?: (value: any) => boolean;
  customError?: CustomFieldErrorParser;
  validationMessage?: string;
  customValidatorMessage?: string;
  onlyDigits?: boolean;
  onChangeCallback?: (newValue?: any) => void;
  tooltipText?: string;
  rules?: RegisterOptions;
  [x: string]: any;
}

type FieldProps<A> = A & Props;

export function withField<T, A>(Input: FC<InputProps<T> & A>, isGroup = false): FC<FieldProps<A>> {
  const Field: FC<FieldProps<A>> = props => {
    const { control } = useFormContext();

    const {
      className,
      id,
      name,
      label,
      description,
      clean = true,
      readOnly,
      labelClassName,
      inputClassName,
      onlyDigits,
      validationMessage,
      customValidatorMessage,
      validator,
      customError,
      required,
      onChangeCallback,
      tooltipText,
      rules,
      ...restProps
    } = props;

    const combinedRules: RegisterOptions = {
      ...rules,
      required: required ? (validationMessage ?? 'Required') : rules?.required,
      validate: validator ? (value: any) => validator(value) || 'validate' : rules?.validate
    };

    const {
      field: { onChange, value },
      fieldState: { error }
    } = useController({
      name,
      control,
      rules: combinedRules,
      defaultValue: '' as any
    });

    return (
      <div className={clsx('flex flex-col gap-1.5', className)}>
        <div className="flex space-x-2">
          {label && (
            <Label className={twMerge(labelClassName)}>
              {label}
              {required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
          )}
          {tooltipText && <Tooltip>{tooltipText}</Tooltip>}
        </div>
        {description && <p>{description}</p>}

        <Input
          {...(restProps as unknown as A)}
          id={id}
          className={twMerge(inputClassName, 'w-full')}
          value={value}
          onChange={(v: any) => {
            let valueToSet = v;
            if (onlyDigits) {
              valueToSet = valueToSet.replace(/\D+/g, '');
            }

            onChange(valueToSet);

            if (onChangeCallback) {
              onChangeCallback(valueToSet);
            }
          }}
          readOnly={readOnly}
        />

        {error?.type === 'validate' && (
          <p className="mt-2 text-xs md:text-sm text-red-600" id={`${name}-validator-error`}>
            {error.message || customValidatorMessage}
          </p>
        )}
        {error?.type === 'required' && (
          <p className="mt-2 text-xs md:text-sm text-red-600" id={`${name}-error`}>
            {error.message || validationMessage}
          </p>
        )}
        {error?.type === 'BEValidation' && (
          <p className="mt-2 text-xs md:text-sm text-red-600" id={`${name}-be-validation-error`}>
            {error.message ?? ''}
          </p>
        )}
        {error?.type === 'custom' && (
          <p className="mt-2 text-xs md:text-sm text-red-600" id={`${name}-custom-error`}>
            {error.message}
          </p>
        )}
      </div>
    );
  };
  return Field;
}
